/**
 * AdminLocationCapture — Verified Location Capture Tool
 * URL: /admin/location-capture
 * Staff can capture, review, and publish verified Jamaica locations.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  ArrowLeft, MapPin, Plus, Check, X, RefreshCw, Loader2, Send,
  Navigation, Crosshair, Clock, AlertTriangle, Eye, Lock, Mail, Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], shadowSize: [41, 41],
});

const PLACE_TYPES = [
  'residential', 'street_address', 'avenue', 'road', 'community', 'scheme',
  'school', 'hospital', 'business', 'plaza', 'restaurant', 'shop',
  'government', 'church', 'transport', 'park', 'landmark', 'general',
];
const PARISHES = ['Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'];
const statusColors = { pending: 'bg-amber-100 text-amber-700', published: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };

function DraggablePin({ position, onMove }) {
  const ref = useRef(null);
  useEffect(() => {
    const m = ref.current;
    if (m) m.on('dragend', () => { const { lat, lng } = m.getLatLng(); onMove(lat, lng); });
  }, [onMove]);
  return <Marker position={position} icon={redIcon} draggable ref={ref} />;
}

function MapClick({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng.lat, e.latlng.lng) });
  return null;
}

export default function AdminLocationCapture() {
  const navigate = useNavigate();
  const [pinVerified, setPinVerified] = useState(() => sessionStorage.getItem('capture_pin_ok') === 'true');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [tab, setTab] = useState('capture');
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');

  const verifyPin = async () => {
    setPinLoading(true);
    setPinError(false);
    try {
      const res = await fetch(`${API_URL}/api/location-capture/verify-pin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem('capture_pin_ok', 'true');
        setPinVerified(true);
      } else {
        setPinError(true);
      }
    } catch (e) {
      setPinError(true);
    }
    setPinLoading(false);
  };

  // Capture form
  const [name, setName] = useState('');
  const [lat, setLat] = useState(18.0157);
  const [lng, setLng] = useState(-76.7898);
  const [parish, setParish] = useState('Kingston');
  const [community, setCommunity] = useState('');
  const [addressText, setAddressText] = useState('');
  const [landmarkText, setLandmarkText] = useState('');
  const [placeType, setPlaceType] = useState('general');
  const [aliases, setAliases] = useState('');
  const [captureMethod, setCaptureMethod] = useState('desk_entry');
  const [notes, setNotes] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await api.get('/location-capture/stats'); setStats(r.data.stats); } catch (e) {}
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/location-capture/submissions?status=${filterStatus}&limit=50`);
      setSubmissions(r.data.submissions || []);
    } catch (e) { toast.error('Failed to load submissions'); }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (tab === 'review') fetchSubmissions(); }, [tab, filterStatus, fetchSubmissions]);

  const useGPS = () => {
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude); setLng(pos.coords.longitude); setCaptureMethod('gps_live'); setGpsLoading(false); toast.success('GPS location captured'); },
      () => { setGpsLoading(false); toast.error('GPS unavailable'); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const aliasArr = aliases.split(',').map(a => a.trim()).filter(Boolean);
      const r = await api.post('/location-capture/submit', {
        name: name.trim(), lat, lng, parish, community, address_text: addressText,
        landmark_text: landmarkText, place_type: placeType, aliases: aliasArr,
        capture_method: captureMethod, notes,
      });
      if (r.data.success) {
        toast.success('Location submitted for review!');
        setName(''); setCommunity(''); setAddressText(''); setLandmarkText(''); setAliases(''); setNotes('');
        fetchStats();
      }
    } catch (e) { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  const handleReview = async (id, action) => {
    try {
      await api.post(`/location-capture/review/${id}`, { action });
      toast.success(action === 'approve' ? 'Published to live search!' : 'Submission rejected');
      fetchSubmissions(); fetchStats();
    } catch (e) { toast.error('Review failed'); }
  };

  // PIN Gate
  if (!pinVerified) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="pin-gate">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white">Location Capture</h1>
            <p className="text-sm text-slate-400 mt-1">Enter PIN to access the portal</p>
          </div>
          <div className="space-y-3">
            <input
              type="password" inputMode="numeric" maxLength={6} placeholder="Enter PIN"
              value={pinInput} onChange={e => { setPinInput(e.target.value); setPinError(false); }}
              onKeyDown={e => { if (e.key === 'Enter') verifyPin(); }}
              className={`w-full px-4 py-3 bg-slate-800 border-2 ${pinError ? 'border-red-500' : 'border-slate-700'} rounded-xl text-white text-center text-2xl tracking-widest font-mono focus:outline-none focus:border-amber-500 transition`}
              data-testid="pin-input" autoFocus
            />
            {pinError && <p className="text-red-400 text-sm text-center">Invalid PIN. Try again.</p>}
            <button onClick={verifyPin} disabled={pinLoading || !pinInput}
              className="w-full py-3 bg-amber-500 text-slate-950 rounded-xl font-bold text-base hover:bg-amber-400 transition disabled:opacity-50"
              data-testid="pin-submit">
              {pinLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Unlock Portal'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-location-capture">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">Location Capture</h1>
            <p className="text-sm text-gray-500">Capture & publish verified Jamaica locations</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
            {[
              ['Total', stats.total_submissions, 'text-gray-700'],
              ['Pending', stats.pending, 'text-amber-600'],
              ['Published', stats.published, 'text-emerald-600'],
              ['Rejected', stats.rejected, 'text-red-500'],
              ['Today', stats.published_today, 'text-blue-600'],
              ['Duplicates', stats.duplicates_flagged, 'text-orange-500'],
            ].map(([label, val, color]) => (
              <Card key={label}><CardContent className="p-3 text-center">
                <p className={`text-lg font-bold ${color}`}>{val}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </CardContent></Card>
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="capture"><Plus className="w-3 h-3 mr-1" />Capture</TabsTrigger>
            <TabsTrigger value="review"><Eye className="w-3 h-3 mr-1" />Review ({stats?.pending || 0})</TabsTrigger>
          </TabsList>

          {/* CAPTURE TAB */}
          <TabsContent value="capture">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">New Location</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {/* Map */}
                <div className="h-48 rounded-xl overflow-hidden border border-gray-200">
                  <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                    <DraggablePin position={[lat, lng]} onMove={(newLat, newLng) => { setLat(newLat); setLng(newLng); setCaptureMethod('map_pin'); }} />
                    <MapClick onClick={(cLat, cLng) => { setLat(cLat); setLng(cLng); setCaptureMethod('map_pin'); }} />
                  </MapContainer>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={useGPS} disabled={gpsLoading}>
                    {gpsLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Crosshair className="w-3 h-3 mr-1" />} Use GPS
                  </Button>
                  <p className="text-xs text-gray-400 self-center font-mono">{lat.toFixed(5)}, {lng.toFixed(5)}</p>
                </div>

                {/* Form */}
                <input type="text" placeholder="Location name *" value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" data-testid="capture-name" />

                <div className="grid grid-cols-2 gap-2">
                  <select value={parish} onChange={e => setParish(e.target.value)} className="px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" data-testid="capture-parish">
                    {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input type="text" placeholder="Community" value={community} onChange={e => setCommunity(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" />
                </div>

                <input type="text" placeholder="Street address" value={addressText} onChange={e => setAddressText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" />

                <input type="text" placeholder="Nearby landmark" value={landmarkText} onChange={e => setLandmarkText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" />

                <div className="grid grid-cols-2 gap-2">
                  <select value={placeType} onChange={e => setPlaceType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" data-testid="capture-type">
                    {PLACE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={captureMethod} onChange={e => setCaptureMethod(e.target.value)} className="px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white">
                    <option value="gps_live">GPS Live</option>
                    <option value="map_pin">Map Pin</option>
                    <option value="desk_entry">Desk Entry</option>
                  </select>
                </div>

                <input type="text" placeholder="Aliases (comma-separated)" value={aliases} onChange={e => setAliases(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white" data-testid="capture-aliases" />

                <textarea placeholder="Notes..." value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm h-16 resize-none" />

                <Button onClick={handleSubmit} disabled={submitting || !name.trim()} className="w-full" data-testid="capture-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Submit for Review
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* REVIEW TAB */}
          <TabsContent value="review">
            <div className="flex gap-2 mb-3">
              {['pending', 'published', 'rejected'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${filterStatus === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              <Button variant="outline" size="sm" onClick={fetchSubmissions} className="ml-auto">
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            <div className="space-y-2">
              {submissions.map(sub => (
                <Card key={sub.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm text-gray-900">{sub.name}</p>
                          <Badge className={statusColors[sub.status] || 'bg-gray-100'}>{sub.status}</Badge>
                          {sub.duplicate_flag && <Badge className="bg-orange-100 text-orange-700">Duplicate</Badge>}
                        </div>
                        <p className="text-xs text-gray-500">{sub.parish} · {sub.community || sub.place_type} · {sub.capture_method}</p>
                        <p className="text-xs text-gray-400 font-mono">{sub.lat?.toFixed(5)}, {sub.lng?.toFixed(5)}</p>
                        {sub.aliases?.length > 0 && <p className="text-xs text-gray-400 mt-0.5">Aliases: {sub.aliases.join(', ')}</p>}
                        {sub.address_text && <p className="text-xs text-gray-500 mt-0.5">{sub.address_text}</p>}
                        {sub.notes && <p className="text-xs text-gray-400 italic mt-0.5">{sub.notes}</p>}
                        <p className="text-xs text-gray-300 mt-1">By {sub.captured_by_name} · {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}</p>

                        {sub.duplicate_flag && (
                          <div className="mt-1.5 p-2 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-xs text-orange-700">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Possible duplicate: {sub.duplicate_flag.existing?.display_name} ({sub.duplicate_flag.type})
                            </p>
                          </div>
                        )}
                      </div>

                      {sub.status === 'pending' && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => handleReview(sub.id, 'approve')}
                            className="p-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200" data-testid={`approve-${sub.id}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleReview(sub.id, 'reject')}
                            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200" data-testid={`reject-${sub.id}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {submissions.length === 0 && !loading && (
                <p className="text-sm text-gray-400 text-center py-8">No {filterStatus} submissions</p>
              )}
              {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
